import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { createConvert, createType, getConverts, getTypes, updateConvert, updateType } from '../api/evaluation.api';
import { IType, IGetTypes, IConvert, IGetConverts } from '../models/evaluation.model';

interface IConvertState {
  types?: IType[];
  converts?: IConvert[];
  isLoading: boolean;
  error?: AxiosError | string;
}

const initialState: IConvertState = {
  types: [],
  converts: [],
  isLoading: false
};

const convertSlicer = createSlice({
  name: 'convert',
  initialState,
  reducers: {
    getTypesStart: (state: IConvertState) => {
      state.types = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getTypesSuccess: (state: IConvertState, action: PayloadAction<IGetTypes>) => {
      state.types = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getTypesFailure: (state: IConvertState, action: PayloadAction<AxiosError|string>) => {
      state.types = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getConvertsStart: (state: IConvertState) => {
      state.converts = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getConvertsSuccess: (state: IConvertState, action: PayloadAction<IGetConverts>) => {
      state.converts = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getConvertsFailure: (state: IConvertState, action: PayloadAction<AxiosError | string>) => {
      state.converts = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: IConvertState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (state: IConvertState, action: PayloadAction<AxiosError|string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: IConvertState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    updateSuccess: (state: IConvertState) => {
      state.isLoading = false;
      state.error = undefined;
    }
  }
});

export const {
  getTypesStart,
  getTypesSuccess,
  getTypesFailure,
  getConvertsStart,
  getConvertsSuccess,
  getConvertsFailure,
  requestStart,
  requestFailure,
  createSuccess,
  updateSuccess
} = convertSlicer.actions;

export default convertSlicer.reducer;

export const doGetTypes = (): any => async (dispatch: any) => {
  try {
    dispatch(getTypesStart());
    const response = await getTypes();
    dispatch(getTypesSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getTypesFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doCreateType = (data: IType): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await createType(data);
    dispatch(createSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doUpdateType = (typeCode: number, data: IType): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await updateType(typeCode, data);
    dispatch(updateSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetConverts = (typeCode: number): any => async (dispatch: any) => {
  try {
    dispatch(getConvertsStart());
    const response = await getConverts(typeCode);
    dispatch(getConvertsSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getConvertsFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doCreateConvert = (typeCode: number, data: IConvert): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await createConvert(typeCode, data);
    dispatch(createSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doUpdateConvert = (conversionCode: number, data: IConvert): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await updateConvert(conversionCode, data);
    dispatch(updateSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};
